import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';

import EbookSubmissionPreviewPane from '../../app/ebookSubmissions/components/EbookSubmissionPreviewPane';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/Button';
import Card from '../../components/Card';
import LoadingModal from '../../components/modal/LoadingModal';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import UploadFileBox from '../../components/UploadFileBox';
import { EbookSubmissionPreviewRowFragment } from '../../core/graphql/types';
import useMutationPreviewEbookSubmission from '../../hooks/useMutationPreviewEbookSubmission';
import useMutationUploadEbookSubmission from '../../hooks/useMutationUploadEbookSubmission';
import { convertFileToBase64 } from '../../utils/blob';

const TEMPLATE_URL_PATH = '../../a/template/Ebook_Submission_Template.xlsx';

const EbookSubmissionCreatePage = () => {
  const history = useHistory();
  const { onMutatePreviewEbookSubmission, isLoadingMutatePreviewEbookSubmission } = useMutationPreviewEbookSubmission();
  const { onMutateUploadEbookSubmission, isLoadingMutateUploadEbookSubmission } = useMutationUploadEbookSubmission();

  const [selectedCSVFile, setSelectedCSVFile] = useState<File | undefined>(undefined);
  const [adminNote, setAdminNote] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<EbookSubmissionPreviewRowFragment[]>([]);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [adminNoteError, setAdminNoteError] = useState<string | undefined>(undefined);

  // Guards against picking file A, then file B before A's preview resolves — without this, A's
  // response can land after B's and overwrite previewId/previewRows with the wrong file's result
  // while the filename box still shows B. Only the response matching the latest request applies.
  const latestPreviewRequestId = useRef(0);

  // Validation now happens server-side only — selecting a file immediately previews it (server
  // validates + returns a per-field projection, and persists a draft to upload later if every
  // row is valid). There's no client-side CSV parsing/validation anymore.
  const onSelectCSVFile = useCallback(
    async (file: File | undefined) => {
      const requestId = (latestPreviewRequestId.current += 1);

      setSelectedCSVFile(file);
      setFileError(undefined);
      setPreviewId(null);
      setPreviewRows([]);

      if (!file) return;

      const fileBase64 = (await convertFileToBase64(file)) as string;
      const res = await onMutatePreviewEbookSubmission({ file: fileBase64, fileName: file.name });

      if (requestId !== latestPreviewRequestId.current) return; // a newer file was selected meanwhile

      if (!res.success) {
        // 'PreviewEbookSubmission' is in errorLink's operationNamesToHideErrorModal (same as
        // upload) — this is the only surfaced message for a genuine failure (corrupt file, Drive
        // outage), not a duplicate of a global alert.
        setFileError("Couldn't validate this file. Choose it again to retry.");
        return;
      }

      setPreviewId(res.preview?._id ?? null);
      setPreviewRows(res.preview?.rows ?? []);

      if (res.preview && res.preview.invalidCount > 0) {
        ToastComponent({ label: `${res.preview.invalidCount} of ${res.preview.totalCount} row(s) need fixing.`, type: IToastType.ERROR });
      }
    },
    [onMutatePreviewEbookSubmission],
  );

  const onChangeAdminNote = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAdminNote(e.target.value);
    setAdminNoteError(undefined);
  }, []);

  const onSubmit = useCallback(async () => {
    let hasError = false;
    if (!previewId) {
      setFileError('Choose a valid CSV file to upload.');
      hasError = true;
    }
    if (!adminNote.trim()) {
      setAdminNoteError('Add a note so other admins know what this batch is.');
      hasError = true;
    }
    if (hasError || !previewId) return;

    const res = await onMutateUploadEbookSubmission({ _id: previewId, adminNote: adminNote.trim() });

    if (res.success) {
      ToastComponent({ label: 'Batch uploaded — processing has started.', type: IToastType.SUCCESS });
      history.push(`/ebookSubmissions/detail?id=${res.ebookSubmission?._id}`);
      return;
    }

    // Clears the whole preview, not just the id — a stale-looking "N books ready" table next to a
    // disabled Upload button (with only a toast, now faded, explaining why) is confusing. Forcing
    // a full re-select keeps what's on screen always consistent with what Upload will actually do.
    ToastComponent({ label: "This batch couldn't be uploaded — choose the CSV again to re-validate it.", type: IToastType.ERROR });
    setPreviewId(null);
    setPreviewRows([]);
    setFileError("This batch couldn't be uploaded. Choose the CSV again to re-validate it.");
  }, [previewId, adminNote, onMutateUploadEbookSubmission, history]);

  const isPreviewing = isLoadingMutatePreviewEbookSubmission;
  const canUpload = !!previewId && !isPreviewing;

  return (
    <div className="space-y-12">
      {isLoadingMutateUploadEbookSubmission && <LoadingModal />}

      <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
        <Breadcrumbs items={[{ name: 'Ebook Submissions', url: '/ebookSubmissions' }, { name: 'Create' }]} />

        <div className="flex justify-between pt-5">
          <h1 className="font-dbh text-[28px]">Create Ebook Submission</h1>

          <div className="flex items-center space-x-12">
            <Link to="/ebookSubmissions">
              <Button type="outline" color="gray">
                Cancel
              </Button>
            </Link>
            <Button type="fill" color="red" onClick={onSubmit} disabled={!canUpload || isLoadingMutateUploadEbookSubmission}>
              Upload
            </Button>
          </div>
        </div>

        <div className="mb-0 divider opacity-10" />
      </div>

      <div className="flex flex-col w-full space-y-24">
        <Card className="space-y-16">
          <div>
            <label className="label">
              <span className="label-text text-systemGrays02LabelSecondary">
                Batch note <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>
            <input
              className={classNames('w-full input input-bordered', adminNoteError && 'input-error')}
              placeholder="e.g. January fantasy novel batch"
              value={adminNote}
              onChange={onChangeAdminNote}
            />
            {adminNoteError && (
              <label className="label !mt-0">
                <span className="label-text-alt text-colorsBrandRed01Primary font-tt">{adminNoteError}</span>
              </label>
            )}
          </div>

          <UploadFileBox
            label="CSV file"
            required
            acceptFileExtension=".csv"
            fileName={selectedCSVFile?.name}
            onSelectFile={onSelectCSVFile}
            hasError={!!fileError}
            helperText={fileError || 'A .csv exported from the ebook submission template. Validated automatically on select.'}
            rightLabelNode={
              <a href={TEMPLATE_URL_PATH} download="Ebook_Submission_Template.xlsx">
                <Button color="red" type="ghost" className="text-colorsBrandRed01Primary">
                  <FiDownload className="text-24" />
                  <p className="ml-4">Download Template</p>
                </Button>
              </a>
            }
          />
        </Card>

        <Card className="flex flex-col min-h-[500px]">
          <EbookSubmissionPreviewPane rows={previewRows} isParsing={isPreviewing} />
        </Card>
      </div>
    </div>
  );
};

export default EbookSubmissionCreatePage;
